import type { ShapeVariant } from '@/configurator/shapeConfig'
import type { ChangeableProperties } from '@/configurator/utils/shapeConfigUtils'
import type { BlueprintDimKey } from '@/configurator/blueprint-overlay/types'

/**
 * The parameter keys the backend accepts on an ERP shape mapping
 * (furnisystems-backend erpShapeMappingValidation.ts ERP_SHAPE_PARAMETER_KEYS).
 * Keep in sync — anything else is rejected on save.
 */
export type ParamKind =
  | { kind: 'count' }
  | { kind: 'cm' }
  | { kind: 'angle' }
  | { kind: 'enum'; values: string[] }
  | { kind: 'boolean' }

export const MAPPING_PARAM_KEYS = {
  seat_sections: { kind: 'count' },
  backrest_sections: { kind: 'count' },
  number_of_big_pillows: { kind: 'count' },
  number_of_small_pillows: { kind: 'count' },
  extendable_part_length: { kind: 'cm' },
  backrest_width: { kind: 'cm' },
  armrest_width: { kind: 'cm' },
  corner_radius: { kind: 'cm' },
  angle: { kind: 'angle' },
  extension_type: {
    kind: 'enum',
    values: ['STANDART', 'MECHANIC_RECLINER', 'ELECTRIC_RECLINER'],
  },
  backrest_type: { kind: 'enum', values: ['STANDART', 'MECHANIC', 'ELECTRIC'] },
  covered_side: { kind: 'boolean' },
  enabled_connectors: { kind: 'boolean' },
} satisfies Record<string, ParamKind>

export type MappingParamKey = keyof typeof MAPPING_PARAM_KEYS
export type MappingParams = Partial<
  Record<MappingParamKey, number | string | boolean | null>
>

export const PARAM_KEY_ORDER = Object.keys(
  MAPPING_PARAM_KEYS
) as MappingParamKey[]

/** Params edited on the drawing (when the type allows them). */
export const ON_BLUEPRINT_PARAM_KEYS: MappingParamKey[] = [
  'backrest_width',
  'armrest_width',
  'extendable_part_length',
  'corner_radius',
  'angle',
]

export const isOnBlueprintParam = (key: string): key is BlueprintDimKey &
  MappingParamKey => (ON_BLUEPRINT_PARAM_KEYS as string[]).includes(key)

/** Keys with no changeable-properties flag — always offered. */
const UNFLAGGED_KEYS: MappingParamKey[] = [
  'seat_sections',
  'backrest_sections',
  'enabled_connectors',
]

export const PARAM_LABELS: Record<MappingParamKey, string> = {
  seat_sections: 'Seat sections',
  backrest_sections: 'Backrest sections',
  number_of_big_pillows: 'Pillows',
  number_of_small_pillows: 'Small pillows',
  extendable_part_length: 'Extension length',
  backrest_width: 'Backrest width',
  armrest_width: 'Armrest width',
  corner_radius: 'Corner radius',
  angle: 'Angle',
  extension_type: 'Extension type',
  backrest_type: 'Backrest type',
  covered_side: 'Covered side',
  enabled_connectors: 'Connectors enabled',
}

/** Whether the type's changeable properties allow this param. */
export const isParamAllowed = (
  key: MappingParamKey,
  changeable: ChangeableProperties | null
): boolean => {
  if (UNFLAGGED_KEYS.includes(key)) return true
  if (!changeable) return false
  return Boolean((changeable as Record<string, boolean | undefined>)[key])
}

/** Accepts the API's Json (object, or the legacy Excel JSON string). */
export const parseParams = (raw: unknown): MappingParams => {
  let value = raw
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value)
    } catch {
      return {}
    }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const out: MappingParams = {}
  for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
    if (!(key in MAPPING_PARAM_KEYS)) continue
    if (v === null || v === undefined || v === '') continue
    out[key as MappingParamKey] = v as number | string | boolean
  }
  return out
}

/** Drops empty values; returns null when nothing is left. */
export const cleanParams = (params: MappingParams): MappingParams | null => {
  const out: MappingParams = {}
  for (const key of PARAM_KEY_ORDER) {
    const v = params[key]
    if (v === null || v === undefined || v === '') continue
    if (typeof v === 'number' && Number.isNaN(v)) continue
    out[key] = v
  }
  return Object.keys(out).length ? out : null
}

const VALUE_LABELS: Record<string, string> = {
  STANDART: 'Standard',
  ELECTRIC_RECLINER: 'Electric recliner',
  MECHANIC_RECLINER: 'Mechanic recliner',
  MECHANIC: 'Mechanic',
  ELECTRIC: 'Electric',
}

export const formatParamValue = (key: MappingParamKey, value: unknown) => {
  const spec = MAPPING_PARAM_KEYS[key]
  if (typeof value === 'boolean') return value ? 'yes' : 'no'
  if (typeof value === 'string') return VALUE_LABELS[value] ?? value
  if (spec.kind === 'cm') return `${value} cm`
  if (spec.kind === 'angle') return `${value}°`
  return String(value)
}

/** Short chips for the list ("Pillows: 2", "Backrest width: 5 cm"). */
export const paramChips = (params: MappingParams): string[] =>
  PARAM_KEY_ORDER.filter(key => params[key] !== undefined).map(
    key => `${PARAM_LABELS[key]}: ${formatParamValue(key, params[key])}`
  )

/** Keeps only the whitelisted keys of a picked shape variant. */
export const variantToParams = (variant?: ShapeVariant): MappingParams =>
  variant ? parseParams(variant) : {}
